import { useEffect, useState } from "react";
import {
  Users,
  Briefcase,
  Clock,
  CheckCircle2,
  DollarSign,
  TrendingUp,
} from "lucide-react";
import { Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

import { api } from "@/lib/auth-context";

function StatCard({ label, value, icon: Icon, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className="bg-white border border-slate-200 rounded-xl p-6"
    >
      <div className="flex items-center justify-between">
        <p className="text-xs tracking-[0.22em] uppercase font-semibold text-slate-500">
          {label}
        </p>
        <span
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: accent || "#0A192F" }}
        >
          <Icon className="w-4 h-4 text-[#C5A059]" />
        </span>
      </div>
      <p className="mt-4 font-heading text-3xl font-bold text-[#0A192F]">
        {value}
      </p>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/admin/analytics")
      .then(({ data }) => setStats(data))
      .finally(() => setLoading(false));
  }, []);

  if (loading)
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-[#0A192F]" />
      </div>
    );

  return (
    <div data-testid="admin-dashboard" className="p-6 lg:p-10 max-w-7xl">
      <p className="text-xs tracking-[0.22em] uppercase font-semibold text-[#C5A059] mb-2">
        Admin Overview
      </p>
      <h1 className="font-heading text-3xl lg:text-4xl font-semibold text-[#0A192F]">
        Platform at a glance
      </h1>

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Cases"
          value={stats?.total_cases ?? 0}
          icon={Briefcase}
          testId="stat-total-cases"
        />
        <StatCard
          label="Cases Complete"
          value={stats?.cases_complete ?? 0}
          icon={CheckCircle2}
          testId="stat-cases-complete"
        />
        <StatCard
          label="Cases Claimed"
          value={stats?.cases_claimed ?? 0}
          icon={TrendingUp}
          testId="stat-cases-claimed"
        />
        <StatCard
          label="Active Subscriptions"
          value={stats?.active_subscriptions ?? 0}
          icon={Users}
          testId="stat-active-subs"
        />
      </div>

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Attorneys"
          value={stats?.total_attorneys ?? 0}
          icon={Users}
          testId="stat-attorneys"
        />
        <StatCard
          label="Law Firms"
          value={stats?.total_law_firms ?? 0}
          icon={Users}
          testId="stat-law-firms"
        />
        <StatCard
          label="Clients"
          value={stats?.total_clients ?? 0}
          icon={Users}
          testId="stat-clients"
        />
        <StatCard
          label="Pending Approvals"
          value={stats?.pending_attorneys ?? 0}
          icon={Clock}
          testId="stat-pending"
        />
      </div>

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Estimated MRR"
          value={`$${(stats?.estimated_mrr || 0).toLocaleString()}`}
          icon={DollarSign}
          testId="stat-mrr"
        />
        <StatCard
          label="Suspended"
          value={stats?.suspended_users ?? 0}
          icon={Clock}
          testId="stat-suspended"
        />
        <StatCard
          label="Locked"
          value={stats?.locked_users ?? 0}
          icon={Clock}
          testId="stat-locked"
        />
        <StatCard
          label="Past Due"
          value={stats?.past_due_subscriptions ?? 0}
          icon={Clock}
          testId="stat-past-due"
        />
      </div>

      <div className="mt-10 grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <p className="text-xs tracking-[0.22em] uppercase font-semibold text-slate-500 mb-4">
            Cases by State
          </p>
          <ul data-testid="cases-by-state" className="space-y-2">
            {(stats?.cases_by_state || []).length === 0 && (
              <li className="text-sm text-slate-400 italic">No data yet.</li>
            )}
            {(stats?.cases_by_state || []).map((s) => (
              <li key={s.state} className="flex justify-between text-sm">
                <span className="font-medium text-[#0A192F]">{s.state}</span>
                <span className="text-slate-600">{s.count}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <p className="text-xs tracking-[0.22em] uppercase font-semibold text-slate-500 mb-4">
            Cases by Type
          </p>
          <ul data-testid="cases-by-type" className="space-y-2">
            {(stats?.cases_by_type || []).length === 0 && (
              <li className="text-sm text-slate-400 italic">No data yet.</li>
            )}
            {(stats?.cases_by_type || []).map((t) => (
              <li key={t.type} className="flex justify-between text-sm">
                <span className="font-medium text-[#0A192F]">{t.type}</span>
                <span className="text-slate-600">{t.count}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-10 flex flex-wrap gap-3">
        <Link to="/admin/users" data-testid="quick-link-users" className="px-4 py-2 rounded-lg bg-[#0A192F] text-white text-sm font-medium">
          Manage Users
        </Link>
        <Link to="/admin/cases" data-testid="quick-link-cases" className="px-4 py-2 rounded-lg bg-white border border-slate-300 text-[#0A192F] text-sm font-medium">
          Browse Cases
        </Link>
      </div>
    </div>
  );
}
